import { BaseService } from './BaseService';
import { BankTransaction, BankReconciliation } from '../types/banking';
import { ValidationError } from '../utils/errors';

// Define interfaces for MongoDB types to avoid direct imports
interface MongoClientType {
  connect: () => Promise<any>;
  db: (name: string) => any;
}

interface ObjectIdType {
  new(id: string): any;
}

// Create placeholder for MongoDB imports
let MongoClient: { new(uri: string): MongoClientType };
let ObjectId: ObjectIdType;

// Use dynamic import for MongoDB in Node.js environment
if (typeof window === 'undefined') {
  // Server-side code
  try {
    const mongodb = require('mongodb');
    MongoClient = mongodb.MongoClient;
    ObjectId = mongodb.ObjectId;
  } catch (e) {
    console.error('Failed to load mongodb:', e);
    // Provide fallback implementations
    MongoClient = class {
      constructor(private uri: string) {}
      async connect() { return this; }
      db(name: string) { return { collection: () => ({}) }; }
    } as any;
    ObjectId = class {
      constructor(id: string) { this.toString = () => id; }
    } as any;
  }
} else {
  // Client-side code - provide mock implementations
  MongoClient = class {
    constructor(private uri: string) {}
    async connect() { return this; }
    db(name: string) {
      return {
        collection: (collectionName: string) => ({
          findOne: async () => null,
          find: () => ({
            toArray: async () => []
          }),
          insertOne: async () => ({ insertedId: 'mock-id' }),
          updateOne: async () => ({})
        })
      };
    }
  } as any;
  
  ObjectId = class {
    constructor(id: string) {
      this.toString = () => id;
    }
  } as any;
}

// S3 import with similar pattern
let S3Client: any;
if (typeof window === 'undefined') {
  try {
    const AWS = require('aws-sdk');
    S3Client = AWS.S3;
  } catch (e) {
    console.error('Failed to load aws-sdk:', e);
    // Provide fallback implementation
    S3Client = class {
      constructor(config: any) {}
    };
  }
} else {
  // Mock S3 for browser
  S3Client = class {
    constructor(config: any) {}
    // Add mock methods as needed
  };
}

interface BookTransaction {
  id: string;
  date: Date;
  amount: number;
  description: string;
  type: 'DEBIT' | 'CREDIT';
  category?: string;
  reference?: string;
  status: 'PENDING' | 'POSTED' | 'RECONCILED';
}

interface ReconciliationMatch {
  bankTransactionId: string;
  bookTransactionId: string;
  confidence: number;
  verified: boolean;
}

interface ReconciliationSession {
  id: string;
  fundId: string;
  bankId: string;
  startDate: Date;
  endDate: Date;
  bankBalance: number;
  bookBalance: number;
  difference: number;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  matches: ReconciliationMatch[];
  exceptions: Array<{
    type: 'UNMATCHED_BANK' | 'UNMATCHED_BOOK' | 'AMOUNT_MISMATCH';
    transactionId: string;
    details: string;
  }>;
}

export class ReconciliationService extends BaseService {
  private static instance: ReconciliationService;
  private mongoClient: any | null = null;
  private s3: any;
  private bucketName: string;

  constructor() {
    super();
    this.s3 = new S3Client({
      region: process.env.AWS_REGION
    });
    this.bucketName = process.env.AWS_RECONCILIATION_BUCKET || 'default-bucket';
  }

  public static getInstance(): ReconciliationService {
    if (!ReconciliationService.instance) {
      ReconciliationService.instance = new ReconciliationService();
    }
    return ReconciliationService.instance;
  }

  private async getMongoClient(): Promise<any> {
    if (!this.mongoClient) {
      if (!process.env.MONGODB_URI) {
        throw new Error('MONGODB_URI environment variable is not set');
      }
      this.mongoClient = new MongoClient(process.env.MONGODB_URI);
      await this.mongoClient.connect();
    }
    return this.mongoClient;
  }

  async getSession(sessionId: string): Promise<ReconciliationSession> {
    const client = await this.getMongoClient();
    const db = client.db('reconciliation');
    
    const result = await db.collection('sessions')
      .findOne({ _id: new ObjectId(sessionId) });
    
    if (!result) {
      throw new Error(`Session ${sessionId} not found`);
    }

    return {
      id: result._id.toString(),
      fundId: result.fundId,
      bankId: result.bankId,
      startDate: new Date(result.startDate),
      endDate: new Date(result.endDate),
      bankBalance: result.bankBalance,
      bookBalance: result.bookBalance,
      difference: result.difference,
      status: result.status,
      matches: result.matches || [],
      exceptions: result.exceptions || []
    };
  }

  async getBookTransactions(
    fundId: string, 
    dateRange: { start: Date; end: Date }
  ): Promise<BookTransaction[]> {
    const client = await this.getMongoClient();
    const db = client.db('reconciliation');
    
    const transactions = await db.collection('book_transactions')
      .find({
        fundId,
        date: {
          $gte: dateRange.start,
          $lte: dateRange.end
        }
      })
      .toArray();

    return transactions.map((tx: any) => ({
      id: tx._id.toString(),
      date: new Date(tx.date),
      amount: tx.amount,
      description: tx.description,
      type: tx.type,
      category: tx.category,
      reference: tx.reference,
      status: tx.status
    }));
  }

  async startSession(params: {
    fundId: string;
    bankId: string;
    dateRange: { start: Date; end: Date };
    bankTransactions: any[];
    bookTransactions: BookTransaction[];
  }): Promise<string> {
    const client = await this.getMongoClient();
    const db = client.db('reconciliation');
    
    const session = await db.collection('sessions').insertOne({
      fundId: params.fundId,
      bankId: params.bankId,
      dateRange: params.dateRange,
      status: 'IN_PROGRESS',
      createdAt: new Date()
    });

    return session.insertedId.toString();
  }

  async autoMatch(sessionId: string): Promise<ReconciliationSession> {
    const client = await this.getMongoClient();
    const db = client.db('reconciliation');
    
    try {
      // Get current session
      const session = await this.getSession(sessionId);
      
      // Perform automated matching logic
      const matches = await this.performAutomatedMatching(session);
      
      // Update session with new matches
      await db.collection('sessions').updateOne(
        { _id: new ObjectId(sessionId) },
        { 
          $set: { 
            matches,
            status: 'IN_PROGRESS'
          }
        }
      );
      
      return this.getSession(sessionId);
    } finally {
      await client.close();
    }
  }

  async manualMatch(
    sessionId: string, 
    match: {
      bankTransactionId: string;
      bookTransactionId: string;
    }
  ): Promise<ReconciliationSession> {
    const client = await this.getMongoClient();
    const db = client.db('reconciliation');
    
    try {
      // Add manual match
      await db.collection('sessions').updateOne(
        { _id: new ObjectId(sessionId) },
        { 
          $push: { 
            matches: {
              ...match,
              confidence: 1,
              verified: true
            }
          }
        }
      );
      
      return this.getSession(sessionId);
    } finally {
      await client.close();
    }
  }

  async completeSession(sessionId: string): Promise<void> {
    await this.withTransaction(async (client) => {
      // Validate session can be completed
      await this.validateSessionCompletion(client, sessionId);
      
      // Mark session as completed
      await this.markSessionComplete(client, sessionId);
    });
  }

  private async createSession(client: any, params: any): Promise<{ id: string }> {
    // Implementation for creating a new session
    return { id: 'session-id' }; // Placeholder
  }

  private async performInitialMatching(client: any, sessionId: string, params: any): Promise<void> {
    // Implementation for initial matching logic
  }

  private async performAutomatedMatching(
    session: ReconciliationSession
  ): Promise<ReconciliationMatch[]> {
    // Implement matching logic here
    // This is a placeholder that returns empty matches
    return [];
  }

  private async recordManualMatch(client: any, sessionId: string, match: any): Promise<void> {
    // Implementation for recording manual matches
  }

  private async validateSessionCompletion(client: any, sessionId: string): Promise<void> {
    // Implementation for validation logic
  }

  private async markSessionComplete(client: any, sessionId: string): Promise<void> {
    // Implementation for completing session
  }
} 